import React, { useState } from 'react';
import { Wind, Droplets, AlertCircle, Activity, ChevronDown, ChevronUp } from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import { AirQualityMetrics, formatIndianNumber } from '../../utils/environmentalCalculations';
import { NatureCard } from '../shared/NatureCard';
import { MetricDisplay } from '../shared/MetricDisplay';
import { BotanicalDivider } from '../shared/BotanicalDivider';
import './AirQualitySection.css';

interface AirQualitySectionProps {
  metrics: AirQualityMetrics;
}

export const AirQualitySection: React.FC<AirQualitySectionProps> = ({ metrics }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const pollutantData = [
    { name: 'PM (Particulate Matter)', value: metrics.pmRemovalKg, color: '#e74c3c' },
    { name: 'NO₂ (Nitrogen Dioxide)', value: metrics.no2RemovalKg, color: '#e67e22' },
    { name: 'O₃ (Ozone)', value: metrics.o3RemovalKg, color: '#f39c12' },
    { name: 'SO₂ (Sulfur Dioxide)', value: metrics.so2RemovalKg, color: '#3498db' },
    { name: 'CO (Carbon Monoxide)', value: metrics.coRemovalKg, color: '#95a5a6' },
  ];

  const pollutantDetails = [
    {
      name: 'PM',
      fullName: 'Particulate Matter',
      amount: metrics.pmRemovalKg.toFixed(2),
      percentage: ((metrics.pmRemovalKg / metrics.totalPollutantsKg) * 100).toFixed(1),
      impact: 'Respiratory diseases, cardiovascular issues',
      mechanism: 'Leaf surface deposition, interception',
      icon: <Droplets size={32} />,
      color: '#e74c3c',
    },
    {
      name: 'NO₂',
      fullName: 'Nitrogen Dioxide',
      amount: metrics.no2RemovalKg.toFixed(2),
      percentage: ((metrics.no2RemovalKg / metrics.totalPollutantsKg) * 100).toFixed(1),
      impact: 'Lung inflammation, reduced immunity',
      mechanism: 'Stomatal absorption',
      icon: <Wind size={32} />,
      color: '#e67e22',
    },
    {
      name: 'O₃',
      fullName: 'Ozone',
      amount: metrics.o3RemovalKg.toFixed(2),
      percentage: ((metrics.o3RemovalKg / metrics.totalPollutantsKg) * 100).toFixed(1),
      impact: 'Respiratory irritation, asthma',
      mechanism: 'Stomatal uptake',
      icon: <Activity size={32} />,
      color: '#f39c12',
    },
  ];

  return (
    <section className="air-quality-section">
      <div className="air-quality-section__container">
        <div
          className="air-quality-section__header"
          onClick={() => setIsExpanded(!isExpanded)}
          style={{ cursor: 'pointer' }}
        >
          <AlertCircle size={48} className="air-quality-section__header-icon" />
          <div style={{ flex: 1 }}>
            <h2 className="air-quality-section__title">Air Quality & Public Health</h2>
            <p className="air-quality-section__subtitle">
              Annual pollutant removal capacity and health benefits
            </p>
          </div>
          {isExpanded ? <ChevronUp size={32} /> : <ChevronDown size={32} />}
        </div>

        {isExpanded && (
          <>

        <BotanicalDivider style="simple" spacing="medium" />

        {/* Context */}
        <div className="air-quality-section__context">
          <div className="air-quality-section__context-icon">🏥</div>
          <div className="air-quality-section__context-content">
            <strong>Public Health Context:</strong> Air pollution is one of the leading environmental
            health risks globally. Urban trees serve as natural air filters, removing harmful pollutants
            before they reach human lungs. The {metrics.totalPollutantsKg.toFixed(0)} kg of pollutants
            removed annually by these trees translates to significant reductions in respiratory diseases,
            asthma attacks, and cardiovascular complications for Pune residents.
          </div>
        </div>

        <BotanicalDivider style="leaf" spacing="medium" />

        {/* Primary Metric */}
        <div className="air-quality-section__primary">
          <NatureCard className="air-quality-section__hero-card" gradient="forest">
            <div className="air-quality-section__hero-value">
              {metrics.totalPollutantsKg.toFixed(0)}
            </div>
            <div className="air-quality-section__hero-label">kg Pollutants Removed Annually</div>
            <div className="air-quality-section__hero-sublabel">
              Improving air quality and reducing respiratory health risks
            </div>
          </NatureCard>
        </div>

        {/* Impact Metrics */}
        <div className="air-quality-section__grid">
          <MetricDisplay
            value={metrics.equivalentAirPurifiers}
            label="Air Purifiers Equivalent"
            icon={<Wind size={32} />}
            comparison="Running 24/7 with HEPA filters"
            color="blue"
            size="medium"
            delay={0.1}
          />

          <MetricDisplay
            value={formatIndianNumber(metrics.healthBenefitValue)}
            label="Health Benefit Value"
            icon={<Activity size={32} />}
            comparison="Annual avoided health costs"
            color="red"
            size="medium"
            delay={0.2}
          />
        </div>
        </>
        )}
      </div>
    </section>
  );
};

export default AirQualitySection;
